import React from 'react';
import { AlertTriangle, CheckCircle, Shield, ShieldOff } from 'lucide-react';
import { accuracyGateHeadline, isAccuracyGateDone, type AccuracyGate } from '../../utils/accuracyGate';

interface AccuracyGateBadgeProps {
  gate?: AccuracyGate | null;
  loading?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  className?: string;
}

const AccuracyGateBadge: React.FC<AccuracyGateBadgeProps> = ({
  gate,
  loading = false,
  size = 'md',
  onClick,
  className = '',
}) => {
  const sizeClasses = size === 'sm'
    ? 'text-xs px-2 py-0.5 gap-1'
    : size === 'lg'
      ? 'text-sm px-3 py-1.5 gap-2'
      : 'text-xs px-2.5 py-1 gap-1.5';
  const iconSize = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';

  if (loading) {
    return (
      <span className={`inline-flex items-center rounded-full bg-gray-100 text-gray-600 border border-gray-200 ${sizeClasses} ${className}`}>
        Verifying…
      </span>
    );
  }

  if (!isAccuracyGateDone(gate)) {
    return (
      <span
        className={`inline-flex items-center rounded-full bg-gray-50 text-gray-500 border border-gray-200 ${sizeClasses} ${className}`}
        title="Accuracy gate has not run"
      >
        <ShieldOff className={iconSize} />
        Unverified
      </span>
    );
  }

  const headline = accuracyGateHeadline(gate);
  const status = gate?.status;
  const clickable = onClick ? 'cursor-pointer' : '';

  if (status === 'passed') {
    return (
      <span
        className={`inline-flex items-center rounded-full bg-green-50 text-green-800 border border-green-200 ${sizeClasses} ${className} ${clickable}`}
        onClick={onClick}
        title={headline}
      >
        <CheckCircle className={iconSize} />
        {headline}
      </span>
    );
  }

  if (status === 'skipped') {
    return (
      <span
        className={`inline-flex items-center rounded-full bg-gray-50 text-gray-600 border border-gray-200 ${sizeClasses} ${className} ${clickable}`}
        onClick={onClick}
        title={headline}
      >
        <Shield className={iconSize} />
        {headline}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center rounded-full bg-amber-50 text-amber-800 border border-amber-200 ${sizeClasses} ${className} ${clickable}`}
      onClick={onClick}
      title={headline}
    >
      <AlertTriangle className={iconSize} />
      {headline}
    </span>
  );
};

export default AccuracyGateBadge;
